<template>
  <div>
    <h1 style="margin-bottom: 30px">Google Classroom Authorization</h1>
    <hilo-button
      v-if="code"
      @click="registerOAuthInstructor"
      label="Return To Classroom"
    ></hilo-button>

    <hilo-button
      v-if="!code"
      @click="setUpGoogleClassroom"
      label="Authenticate Google Classroom"
      style="margin-left: 8px"
    ></hilo-button>

    <hilo-select
      v-if="!code"
      ref="Classrooms"
      v-model="selected"
      :searchable="true"
      :options="classes"
      :placeholder="'Classroom'"
      :theme="'grey'"
      label-key="name"
      value-key="id"
      @change="logSelected"
      class="dropdown"
    />

    <div class="googleClassroomInstructions">
      <h2>Steps to Set Up Google Classroom</h2>
      <h3>Step 1: Create a Google Cloud Project</h3>
      <p>
        1. Go to the
        <a href="https://console.cloud.google.com/" target="_blank"
          >Google Cloud Console</a
        >.<br />
        2. Click on the <strong>Select a Project</strong> dropdown at the top of
        the page.<br />
        3. Click <strong>New Project</strong>.<br />
        4. Enter a project name (e.g., "Google Classroom Integration").<br />
        5. Optionally, choose an organization and location, then click
        <strong>Create</strong>.<br />
        6. Wait for the project to be created and select it from the dropdown.
      </p>

      <h3>Step 2: Enable Google Classroom API</h3>
      <p>
        1. With your project selected, go to the
        <strong>API & Services</strong> dashboard.<br />
        2. Click <strong>Enable APIs and Services</strong> at the top.<br />
        3. Search for "Google Classroom API" in the API library.<br />
        4. Select it and click <strong>Enable</strong>.<br />
        5. You will be redirected to the Classroom API overview page.
      </p>

      <h3>Step 3: Configure OAuth Consent Screen</h3>
      <p>
        1. In the Google Cloud Console, navigate to
        <strong>API & Services > OAuth Consent Screen</strong>.<br />
        2. Choose your user type:<br />
        - <strong>Internal</strong>: For apps used only within your Google
        Workspace organization.<br />
        - <strong>External</strong>: For apps intended for public use.<br />
        3. Click <strong>Create</strong>.<br />
        4. Fill in the necessary details:<br />
        - <strong>App Name</strong>: Enter a recognizable name for your app.<br />
        - <strong>User Support Email</strong>: Add a contact email address.<br />
        - <strong>Developer Contact Information</strong>: Provide at least one
        email.<br />
        5. Click <strong>Save and Continue</strong>.<br />
        6. Add the <strong>Scopes</strong> for the Classroom API:<br />
        - Click <strong>Add or Remove Scopes</strong>.<br />
        - Select the scopes required for your app, such as:<br />
        <code>https://www.googleapis.com/auth/classroom.courses</code><br />
        <code
          >https://www.googleapis.com/auth/classroom.coursework.students</code
        ><br />
        (Adjust based on your app's requirements.)<br />
        7. Click <strong>Save and Continue</strong>.<br />
        8. Skip <strong>Test Users</strong> for now and click
        <strong>Save and Continue</strong>.<br />
        9. Click <strong>Back to Dashboard</strong>.
      </p>

      <h3>Step 4: Create OAuth 2.0 Credentials</h3>
      <p>
        1. Navigate to <strong>API & Services > Credentials</strong>.<br />
        2. Click <strong>Create Credentials</strong> and select
        <strong>OAuth Client ID</strong>.<br />
        3. Choose the <strong>Application Type</strong>:<br />
        - <strong>Web Application</strong>: For web-based apps.<br />
        - <strong>Desktop App</strong>: For standalone desktop applications.<br />
        4. Fill in the required fields:<br />
        - <strong>Name</strong>: Provide a descriptive name.<br />
        - <strong>Authorized Redirect URIs</strong>: Enter a URL where Google
        can redirect users after authentication (e.g.,
        <code>http://localhost:8080</code> during development).<br />
        5. Click <strong>Create</strong>.<br />
        6. Copy the <strong>Client ID</strong> and
        <strong>Client Secret</strong>; you’ll need these for authentication.
      </p>
      <h3>Step 5: Copy Client ID and Client Secret to the Webpage Form</h3>
      <p>
        1. Once you have the <strong>Client ID</strong> and
        <strong>Client Secret</strong>, navigate to the form on this webpage.<br />
        2. Paste the <strong>Client ID</strong> into the designated field.<br />
        3. Paste the <strong>Client Secret</strong> into the designated
        field.<br />
        4. Enter the <strong>Name of the Classroom</strong> into the
        corresponding field.<br />
        5. Click the <strong>Submit</strong> button to save the details to the
        database.
      </p>

      <h3>Step 6: Select the Classroom for Authorization</h3>
      <p>
        1. After submitting the form, navigate to the dropdown menu on this
        webpage.<br />
        2. Select the name of the classroom you want to authorize from the
        dropdown menu.<br />
        3. Ensure that the correct classroom name is chosen to proceed with
        authorization.
      </p>

      <h3>Step 7: Choose the Google Account for Authorization</h3>
      <p>
        1. Once the classroom is selected, click on the
        <strong>Authorize</strong> button.<br />
        2. A Google login window will appear. Log in using the Google account
        associated with the classroom you are authorizing.<br />
        3. Review the permissions requested by the app and click
        <strong>Allow</strong> to grant access.
      </p>

      <h3>Step 8: Confirm Authorization</h3>
      <p>
        1. After granting permissions, you will be redirected back to this
        webpage.<br />
        2. Look for the button labeled <strong>Return to Classroom</strong> and
        click it to finalize the authorization process.<br />
        3. The authorization is now complete, and the classroom is ready for API
        access.
      </p>
    </div>

    <div>
      <h2>Create Classroom Info</h2>
      <form @submit.prevent="handleSubmit">
        <label for="className">Class Name:</label>
        <input
          type="text"
          id="className"
          v-model="formData.classroomName"
          required
        />

        <label for="clientID">Client ID:</label>
        <input type="text" id="clientID" v-model="formData.clientID" required />

        <label for="clientSecret">Client Secret:</label>
        <input
          type="text"
          id="clientSecret"
          v-model="formData.secretKey"
          required
        />

        <button type="submit">Submit</button>
      </form>
    </div>
  </div>
</template>

<script>
import googleClassroomAPIService from "../../../servicehandlers/GoogleClassroomAPIService";
import UsersAPIService from "../../../servicehandlers/UsersAPIService";
import HiloButton from "../../../components/HiloComponents/HiloButton.vue";
import HiloSelect from "../../../components/HiloComponents/HiloSelect.vue";
import { useLoginStore } from "../../../stores/loginStore";

const googleClassroomAPI = new googleClassroomAPIService();
const userAPI = new UsersAPIService();

export default {
  name: "ClassroomOAuth",
  components: { HiloSelect, HiloButton },
  data() {
    return {
      id: null,
      userType: null,
      code: null,
      googleClassroomAccessToken: null,
      selected: null,
      selectedClassroomData: null,
      classes: [],
      formData: {
        classroomName: "",
        clientID: "",
        secretKey: "",
      },
    };
  },
  setup() {
    const store = useLoginStore();
    return { store };
  },
  mounted() {
    this.id = this.store.getUser.id;
    this.userType = this.store.getUser.userType?.name;
    this.code = this.$route.query.code;
    this.listClasses();
  },
  methods: {
    useLoginStore,
    async registerOAuthInstructor() {
      const encodedCode = encodeURIComponent(this.code);
      if (!this.code) {
        return;
      }

      try {
        const response = await googleClassroomAPI.authCallbackCode(
          this.$router,
          `code=${encodedCode}`,
        );
        this.googleClassroomAccessToken = response.tokens.access_token;

        let formData = {
          googleClassroomAccessToken: this.googleClassroomAccessToken,
        };
        await userAPI.update(this.id, formData);

        this.$router.push({ name: "Classroom Dashboard" });
      } catch (error) {}
    },
    async setUpGoogleClassroom() {
      let response;
      response = await googleClassroomAPI.authCallback(
        this.$router,
        this.selectedClassroomData,
      );
      window.location.href = response.authUrl;
    },
    async listClasses() {
      try {
        const response = await googleClassroomAPI.getClassroomInfo();
        for (let index = 0; index < response.length; index++) {
          let id = response[index].id;
          let name = response[index].classroomName;

          let placeholder = { id, name, ...response[index] };

          this.classes.push(placeholder);
        }
      } catch (error) {}
      return this.classes;
    },
    logSelected() {
      const selectedClassroom = this.classes.find(
        (classroom) => classroom.id === this.selected,
      );
      this.selectedClassroomData = selectedClassroom;
    },
    async handleSubmit() {
      try {
        const response = await googleClassroomAPI.createClassroomInfo(
          this.formData,
          this.$router,
        );

        this.formData = {
          classroomName: "",
          clientID: "",
          secretKey: "",
        };
      } catch (error) {}
    },
  },
};
</script>

<style scoped>
.dropdown {
  font-size: 14px;
  border-radius: 4px;
  padding: 3px 16px;
  display: inline-flex;
  margin-left: 16px;
}
</style>
